import { Web3ReactHooks } from '@web3-react/core';
import { Connector } from '@web3-react/types';

// v8 compatible interface (replaces old web3ReactInterface)
export interface Web3ReactState {
  connector?: Connector;
  chainId?: number;
  accounts?: string[];
  activating: boolean;
  error?: Error;
}

// Enhanced compatibility interface that matches v6 patterns
export interface LegacyWeb3ReactInterface {
  account?: string;
  chainId?: number;
  active: boolean;
  error?: Error;
  connector?: Connector;
  activating: boolean;
  // Raw EIP-1193 provider for direct web3 operations
  provider?: unknown;
  // v6 methods that components expect
  activate: (connector: Connector) => Promise<void>;
  deactivate: () => void;
}

// Helper hook to maintain v6-like interface
export const useWeb3ReactCompat = (
  hooks: Web3ReactHooks
): Web3ReactState & {
  account?: string;
  active: boolean;
  activate: (connector: Connector) => Promise<void>;
  deactivate: () => void;
} => {
  const { useChainId, useAccounts, useIsActivating, useIsActive } = hooks;

  const chainId = useChainId();
  const accounts = useAccounts();
  const activating = useIsActivating();
  const isActive = useIsActive();

  return {
    chainId,
    accounts,
    account: accounts?.[0],
    activating,
    active: Boolean(isActive && accounts?.length),
    activate: async (connector: Connector) => {
      try {
        await connector.activate();
      } catch (activationError) {
        // eslint-disable-next-line no-console
        console.error('Connector activation failed:', activationError);
        throw activationError;
      }
    },
    deactivate: () => {
      // v8 connectors handle deactivation differently
      // This will be handled by individual connector deactivation
    },
  };
};

// Wallet connection status enum
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error',
}

// Error types for better error handling
export enum Web3Error {
  NO_PROVIDER = 'NO_PROVIDER',
  UNAUTHORIZED = 'UNAUTHORIZED',
  UNSUPPORTED_CHAIN = 'UNSUPPORTED_CHAIN',
  USER_REJECTED = 'USER_REJECTED',
  ACTIVATION_ERROR = 'ACTIVATION_ERROR',
}

// Connector metadata for UI display
export interface ConnectorMetadata {
  name: string;
  logo: string;
  description: string;
  href?: string;
}

// Network information interface
export interface NetworkInfo {
  chainId: number;
  name: string;
  currency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrl: string;
  blockExplorerUrl: string;
}

// Export common network configurations
export const PULSECHAIN_MAINNET: NetworkInfo = {
  chainId: 369,
  name: 'PulseChain',
  currency: {
    name: 'Pulse',
    symbol: 'PLS',
    decimals: 18,
  },
  rpcUrl: 'https://rpc.pulsechain.com',
  blockExplorerUrl: 'https://scan.pulsechain.com',
};

export const PULSECHAIN_TESTNET_V4: NetworkInfo = {
  chainId: 943,
  name: 'PulseChain Testnet v4',
  currency: {
    name: 'Test Pulse',
    symbol: 'tPLS',
    decimals: 18,
  },
  rpcUrl: 'https://rpc.v4.testnet.pulsechain.com',
  blockExplorerUrl: 'https://scan.v4.testnet.pulsechain.com',
};
